import { Injectable, inject, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { materialsState } from '../../state/materials.state';
import { TranslationService } from './translation.service';

// Mapa de materiales con SEO data (fallback for materials without API data)
export interface MaterialSeoData {
    id: string;
    slug: string;
    title: string;
    description: string;
    keywords: string[];
}

@Injectable({
    providedIn: 'root'
})
export class SeoService {
    private doc = inject(DOCUMENT);
    private meta = inject(Meta);
    private titleService = inject(Title);
    private router = inject(Router);
    private t = inject(TranslationService);

    private get defaultTitle(): string {
        return this.t.instant('seo.defaultTitle');
    }
    private get defaultDescription(): string {
        return this.t.instant('seo.defaultDescription');
    }
    private readonly siteUrl = 'https://remateri.ar';
    private readonly defaultImage = 'https://remateri.ar/assets/img/og-image.png';

    constructor() {
        this.setupRouteListener();
        // Set canonical for the initial route (important for prerendering)
        this.updateCanonicalUrl();
    }

    private setupRouteListener(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.updateCanonicalUrl();
        });
    }

    /**
     * Obtiene el slug SEO-friendly a partir del ID del material
     * Uses dynamic materials from state (loaded from API/JSON)
     */
    public getSlugFromMaterialId(materialId: string): string | null {
        const materials = materialsState.availableMaterials();
        const material = materials.find(m => m.id === materialId);
        return material?.slug || null;
    }

    /**
     * Obtiene el ID del material a partir del slug
     * Uses dynamic materials from state (loaded from API/JSON)
     */
    public getMaterialIdFromSlug(slug: string): string | null {
        const materials = materialsState.availableMaterials();
        const material = materials.find(m => m.slug === slug);
        return material?.id || null;
    }

    /**
     * Configura SEO para una página de material
     * Uses material data from state with fallback SEO data
     */
    public setMaterialSeo(slug: string): void {
        const materials = materialsState.availableMaterials();
        const material = materials.find(m => m.slug === slug);
        
        if (!material) {
            console.warn(`Material with slug "${slug}" not found for SEO`);
            return;
        }

        // Get fallback SEO data if available (from i18n translations)
        const fallbackSeo = this.getMaterialSeoFallback(slug);
        
        // Build title: prefer fallback title, else generate from material name
        const materialName = material.label || material.name || slug;
        const title = fallbackSeo?.title || this.t.instant('seo.howToRecycle', { material: materialName });
        const fullTitle = `${title} | Remateriar`;
        
        // Build description: prefer material.description, then fallback
        const description = material.description || fallbackSeo?.description || this.t.instant('seo.materialFallback', { material: materialName });
        
        // Keywords from fallback or generate basic ones
        const keywords = fallbackSeo?.keywords || this.t.instant('seo.defaultKeywords', { material: materialName }).split(', ');

        this.setTitle(fullTitle);
        this.setMetaDescription(description);
        this.setKeywords(keywords);
        this.setOgTags(fullTitle, description, this.defaultImage);
        this.setTwitterTags(fullTitle, description, this.defaultImage);
        this.setRobots('index, follow');
        this.updateCanonicalUrl();
    }

    public setSeoData(title: string, description: string, keywords: string[] = []): void {
        this.setTitle(title);
        this.setMetaDescription(description);
        this.setKeywords(keywords);
        this.setOgTags(title, description, this.defaultImage);
        this.setTwitterTags(title, description, this.defaultImage);
        this.updateCanonicalUrl();
    }

    public setTitle(title: string): void {
        const newTitle = title || this.defaultTitle;
        this.titleService.setTitle(newTitle);
    }

    public setMetaDescription(description: string): void {
        const desc = description || this.defaultDescription;
        this.meta.updateTag({ name: 'description', content: desc });
    }

    public setKeywords(keywords: string[]): void {
        if (keywords && keywords.length > 0) {
            this.meta.updateTag({ name: 'keywords', content: keywords.join(', ') });
        }
    }

    public setRobots(content: string): void {
        this.meta.updateTag({ name: 'robots', content });
    }

    public setOgTags(title: string, description: string, image?: string): void {
        this.meta.updateTag({ property: 'og:title', content: title || this.defaultTitle });
        this.meta.updateTag({ property: 'og:description', content: description || this.defaultDescription });
        this.meta.updateTag({ property: 'og:url', content: this.siteUrl + this.router.url });
        this.meta.updateTag({ property: 'og:type', content: 'website' });
        this.meta.updateTag({ property: 'og:image', content: image || this.defaultImage });
        this.meta.updateTag({ property: 'og:locale', content: 'es_AR' });
        this.meta.updateTag({ property: 'og:site_name', content: 'Remateriar' });
    }

    public setTwitterTags(title: string, description: string, image?: string): void {
        this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.meta.updateTag({ name: 'twitter:title', content: title || this.defaultTitle });
        this.meta.updateTag({ name: 'twitter:description', content: description || this.defaultDescription });
        this.meta.updateTag({ name: 'twitter:image', content: image || this.defaultImage });
    }

    /**
     * Get translated SEO fallback data for a material slug.
     * Returns null if no translations exist for this slug.
     */
    private getMaterialSeoFallback(slug: string): Omit<MaterialSeoData, 'id' | 'slug'> | null {
        const titleKey = `seo.materials.${slug}.title`;
        const title = this.t.instant(titleKey);
        if (title === titleKey) return null; // No translation found for this slug

        const description = this.t.instant(`seo.materials.${slug}.description`);
        const keywordsStr = this.t.instant(`seo.materials.${slug}.keywords`);
        const keywords = keywordsStr.split(', ');

        return { title, description, keywords };
    }

    private updateCanonicalUrl(): void {
        const link: HTMLLinkElement = this.doc.querySelector('link[rel="canonical"]') || this.doc.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', this.siteUrl + this.router.url);
        if (!link.parentElement) {
            this.doc.head.appendChild(link);
        }
    }
}
